import { Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { RefreshCcw01Icon } from '@/components/icons/RefreshCcw01Icon';
import { XIcon } from '@/components/icons/XIcon';
import {
  DisplayTable,
  DisplayTableColumn,
  StyledTableCell,
} from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useMissingDocumentsContext } from '@/modules/missingDocuments/contexts/MissingDocumentsContext';
import {
  HouseholdMissingDocumentFragment,
  Operations,
  useCreateMissingDocumentOverrideMutation,
  useRestoreDismissedMissingDocumentsMutation,
} from '@/modules/missingDocuments/graphql/MissingDocuments.generated';
import { getDocumentTypeDisplayName } from '@/modules/missingDocuments/missingDocuments.utils';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';
import {
  DocumentType,
  MissingDocumentOverrideDocumentType,
} from '@/types/schema';

import { HargroveConsultationButton } from './HargroveConsultationButton';

function getSectionColumns(label: string): DisplayTableColumn[] {
  return [
    { headerName: label, width: '80%' },
    { headerName: '', width: '20%', align: 'right' },
  ];
}

export function MissingDocumentsPanel() {
  const { isShowing, hide, loading, count } = useMissingDocumentsContext();

  if ((loading && !isShowing) || count == 0) {
    return null;
  }

  return (
    <SideDrawer
      isOpen={isShowing}
      onClose={hide}
      width={OVERLAY_MD}
      heading="Missing Documents"
      body={<MissingDocumentsPanelBody />}
      bodyPropsSx={{ px: 2, pt: 2, pb: 2 }}
      data-testid="MissingDocumentsDrawer"
    />
  );
}

function MissingDocumentsPanelBody() {
  const {
    householdMissingDocuments,
    primaryClientProfileMissingDocuments,
    otherClientProfileMissingDocuments,
    hasOverrides,
  } = useMissingDocumentsContext();

  const { householdId } = useHouseholdDetailsContext();

  const [restoreDismissedMissingDocuments, { loading }] =
    useRestoreDismissedMissingDocumentsMutation({
      variables: {
        householdID: householdId!,
      },
      refetchQueries: [Operations.Query.GetHouseholdMissingDocuments],
    });

  return (
    <Stack direction="column" gap={2}>
      <Typography variant="body-s" color={COLORS.TEXT.SECONDARY}>
        Essential documents were not found for the household and following
        individuals:
      </Typography>
      <MissingDocumentsTableSection
        label="Household"
        missingDocuments={householdMissingDocuments}
      />
      {Object.keys(primaryClientProfileMissingDocuments).map(
        (clientProfile) => {
          const docs =
            primaryClientProfileMissingDocuments[clientProfile] ?? [];
          // Safe to access [0] because we skip empty arrays via the section component.
          const label = docs[0]?.clientProfile?.displayName ?? clientProfile;
          return (
            <MissingDocumentsTableSection
              key={`${clientProfile}-missing-documents`}
              label={label}
              missingDocuments={docs}
            />
          );
        }
      )}
      {Object.keys(otherClientProfileMissingDocuments).map((clientProfile) => {
        const docs = otherClientProfileMissingDocuments[clientProfile] ?? [];
        const label = docs[0]?.clientProfile?.displayName ?? clientProfile;
        return (
          <MissingDocumentsTableSection
            key={`${clientProfile}-missing-documents`}
            label={label}
            missingDocuments={docs}
          />
        );
      })}
      {hasOverrides && (
        <Button
          startIcon={RefreshCcw01Icon}
          variant="secondary"
          size="sm"
          onClick={() => restoreDismissedMissingDocuments()}
          loading={loading}
        >
          Restore missing documents
        </Button>
      )}
      <HargroveConsultationButton fullWidth />
    </Stack>
  );
}

interface MissingDocumentsTableSectionProps {
  label: string;
  missingDocuments: HouseholdMissingDocumentFragment[];
}

function MissingDocumentsTableSection({
  label,
  missingDocuments,
}: MissingDocumentsTableSectionProps) {
  if (missingDocuments.length === 0) {
    return null;
  }

  const clientProfileID = missingDocuments[0]?.clientProfile?.id;

  const columns = getSectionColumns(label);

  return (
    <DisplayTable columns={columns} tableSx={{ tableLayout: 'fixed' }}>
      {missingDocuments.map((doc) => {
        const documentType = doc.documentType as unknown as DocumentType;
        return (
          <MissingDocumentRow
            key={`missing-document-${documentType}-${clientProfileID ?? 'household'}`}
            documentType={documentType}
            clientProfileID={clientProfileID}
          />
        );
      })}
    </DisplayTable>
  );
}

interface MissingDocumentRowProps {
  documentType: DocumentType;
  clientProfileID?: string;
}

function MissingDocumentRow({
  documentType,
  clientProfileID,
}: MissingDocumentRowProps) {
  const { householdId } = useHouseholdDetailsContext();

  const [createMissingDocumentOverride] =
    useCreateMissingDocumentOverrideMutation({
      refetchQueries: [Operations.Query.GetHouseholdMissingDocuments],
    });

  return (
    <StyledTableRow>
      <StyledTableCell>
        <Typography variant="title-s">
          {getDocumentTypeDisplayName(documentType)}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="right">
        <IconButton
          variant="transparent"
          icon={XIcon}
          ariaLabel={`Dismiss ${getDocumentTypeDisplayName(documentType)}`}
          size="xs"
          onClick={() =>
            createMissingDocumentOverride({
              variables: {
                householdID: householdId!,
                documentType:
                  documentType as unknown as MissingDocumentOverrideDocumentType,
                clientProfileID: clientProfileID,
              },
            })
          }
        />
      </StyledTableCell>
    </StyledTableRow>
  );
}
